import React, { useRef, useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { Loader2, MessageSquare, User as UserIcon } from 'lucide-react';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/auth';
import ChatHeader from '@/components/messages/ChatHeader';
import MessageItem from '@/components/messages/MessageItem';
import MessageInput from '@/components/messages/MessageInput';
import { UIMessage } from '@/hooks/useMessages';
import BookingDialog from '@/components/messages/BookingDialog';
import UserProfileModal from './UserProfileModal';
import { fetchUserProfile } from '@/services/profileService';
import { markSpecificMessagesAsRead } from '@/services/messageService';
import { checkBlockStatus } from '@/services/blockService';
import { usePresence } from '@/context/presence-context';

// Définir une interface pour chatPartner
interface ChatPartner {
  id: string;
  name: string;
  avatar: string | null;
  type: 'client' | 'escort';
  isVerified: boolean;
}

interface UserProfileData {
  id: string;
  display_name: string | null;
  created_at: string | null;
  is_verified_client?: boolean | null;
  avatar?: string | null; 
}

interface MessageDetailProps {
  // Utiliser l'interface ChatPartner
  chatPartner: ChatPartner;
  messages: UIMessage[];
  isLoading: boolean;
  onSendMessage: (message: string) => Promise<void>;
  deleteMessage?: (messageId: string) => Promise<void>;
  onOpenBookingModal: () => void;
  userId?: string;
  isMobile?: boolean;
  onBack?: () => void;
}

const MessageDetail: React.FC<MessageDetailProps> = ({
  chatPartner,
  messages,
  isLoading,
  onSendMessage,
  deleteMessage,
  onOpenBookingModal,
  userId,
  isMobile = false,
  onBack
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [isBookingDialogOpen, setIsBookingDialogOpen] = React.useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { onlineUsers } = usePresence();

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [clientProfile, setClientProfile] = useState<UserProfileData | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [blockStatus, setBlockStatus] = useState<'blocked_by_partner' | 'blocked_by_user' | 'none' | 'checking'>('checking');

  const isPartnerOnline = chatPartner?.id ? onlineUsers.has(chatPartner.id) : false;

  // Référence directe au conteneur de messages
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  // NOUVELLE APPROCHE 100% NATIVE
  // Fonction de défilement simple mais efficace
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      console.log('scrollIntoView called on messagesEndRef');
    }
  }, []);

  // Premier effet: défilement automatique à chaque changement de messages
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      // Défilement immédiat
      scrollToBottom();
      
      // Défilement différé pour s'assurer que tout est bien rendu
      const timer = setTimeout(scrollToBottom, 300);
      return () => clearTimeout(timer);
    }
  }, [messages, isLoading, scrollToBottom]);

  // Deuxième effet: observer les mutations pour détecter les changements dans le DOM
  useEffect(() => {
    if (chatContainerRef.current && messages.length > 0) {
      // Créer un MutationObserver pour détecter les changements dans le DOM
      const observer = new MutationObserver(() => {
        scrollToBottom();
      });
      
      // Observer les changements dans le conteneur de chat
      observer.observe(chatContainerRef.current, { 
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });
      
      // Nettoyage
      return () => observer.disconnect();
    }
  }, [messages.length, scrollToBottom]);

  // Intersection Observer pour marquer les messages comme lus
  useEffect(() => {
    if (!chatContainerRef.current || !userId) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const messagesToMarkAsRead: string[] = [];
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const messageId = (entry.target as HTMLElement).dataset.messageId;
            const messageIsUnread = (entry.target as HTMLElement).dataset.isUnread === 'true';
            const messageRecipientId = (entry.target as HTMLElement).dataset.recipientId;
            
            if (messageId && messageIsUnread && messageRecipientId === userId) {
              messagesToMarkAsRead.push(messageId);
            }
          }
        });

        if (messagesToMarkAsRead.length > 0) {
          markSpecificMessagesAsRead(messagesToMarkAsRead)
            .catch(error => console.error("Failed to mark messages as read:", error));
        }
      },
      { threshold: 0.6 }
    );

    // Observer tous les messages
    const messageElements = chatContainerRef.current.querySelectorAll('[data-message-id]');
    messageElements.forEach(el => observer.observe(el));

    return () => {
      messageElements.forEach(el => observer.unobserve(el));
    };
  }, [messages, userId]);

  const fetchAndSetBlockStatus = useCallback(async () => {
    if (userId && chatPartner?.id) {
      setBlockStatus('checking');
      try {
        const status = await checkBlockStatus(userId, chatPartner.id);
        setBlockStatus(status);
      } catch (err) {
        console.error("Failed to check block status:", err);
        setBlockStatus('none');
        toast({ title: t('common.error'), description: t('messages.block_status_error'), variant: 'destructive' });
      }
    } else {
      setBlockStatus('none');
    }
  }, [userId, chatPartner?.id, toast, t]);

  useEffect(() => {
    fetchAndSetBlockStatus();
  }, [fetchAndSetBlockStatus]);

  const handleViewProfile = async () => {
    if (!chatPartner || chatPartner.type !== 'client') return;
    
    setIsLoadingProfile(true);
    setIsProfileModalOpen(true);
    
    if (!clientProfile || clientProfile.id !== chatPartner.id) {
      try {
        const profileData = await fetchUserProfile(chatPartner.id);
        if (profileData) {
           setClientProfile({
            ...profileData, 
            avatar: chatPartner.avatar
           });
        } else {
           toast({ title: t('common.error'), description: t('profile.error_loading_profile'), variant: 'destructive' });
           setIsProfileModalOpen(false);
        }
      } catch (error) {
        console.error("Error fetching client profile:", error);
        toast({ title: t('common.error'), description: t('profile.error_loading_profile'), variant: 'destructive' });
        setIsProfileModalOpen(false);
      } finally {
        setIsLoadingProfile(false);
      }
    }
  };

  const handleOpenBookingDialog = () => {
    setIsBookingDialogOpen(true);
  };

  const handleCloseBookingDialog = () => {
    setIsBookingDialogOpen(false);
  };

  const handleBookAppointment = async (date: Date, timeSlot: string, duration: number) => {
    try {
      if (typeof onSendMessage === 'function') {
        const appointmentMessage = `Demande de rendez-vous pour le ${date.toLocaleDateString()} à ${timeSlot} (durée: ${duration} minutes)`;
        await onSendMessage(appointmentMessage);
        setIsBookingDialogOpen(false);
        toast({
          title: t('messages.booking_success'),
          description: t('messages.booking_pending'),
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast({
        title: t('common.error'),
        description: t('common.error_occurred'),
        variant: 'destructive',
      });
    }
  };

  if (!chatPartner && blockStatus === 'checking') {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t('Loading conversation...')}</h2>
        <p className="text-muted-foreground">{t('Please wait while we fetch your chat')}</p>
      </div>
    );
  }

  const shouldShowViewProfileButton = currentUser?.role === 'escort' && chatPartner?.type === 'client';

  const isInputDisabled = blockStatus === 'blocked_by_partner' || blockStatus === 'blocked_by_user';

  return (
    <>
      <ChatHeader 
        name={chatPartner.name}
        avatar={chatPartner.avatar}
        type={chatPartner.type}
        isPartnerOnline={isPartnerOnline}
        isVerified={chatPartner.isVerified}
        onBookAppointment={onOpenBookingModal}
        onBack={onBack}
        isMobile={isMobile}
        onViewProfile={shouldShowViewProfileButton ? handleViewProfile : undefined}
        blockStatus={blockStatus}
        partnerId={chatPartner?.id}
        refreshBlockStatus={fetchAndSetBlockStatus}
      />
      
      {blockStatus === 'blocked_by_user' && (
        <div className="p-2 bg-yellow-100 text-yellow-800 text-sm text-center border-b border-yellow-200">
          {t('messages.you_blocked', { name: chatPartner?.name || 'User' })}
        </div>
      )}
      {blockStatus === 'blocked_by_partner' && (
        <div className="p-2 bg-red-100 text-red-800 text-sm text-center border-b border-red-200">
          {t('messages.user_blocked_you', { name: chatPartner?.name || 'User' })}
        </div>
      )}

      {/* Conteneur de messages complètement natif avec défilement automatique */}
      <div 
        className={`${isMobile ? 'h-[calc(100vh-300px)]' : 'h-[calc(100vh-230px)]'} overflow-y-auto p-4 ${isInputDisabled ? 'opacity-50' : ''}`}
        ref={chatContainerRef}
        id="chat-container"
      >
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <MessageSquare className="w-12 h-12 text-muted-foreground mb-3" />
            <h3 className="text-lg font-medium mb-1">{t('No messages yet')}</h3>
            <p className="text-muted-foreground text-sm max-w-xs">
              {t('Start the conversation with')} {chatPartner.name} {t('by sending a message below.')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <MessageItem
                key={message.id}
                data-message-id={message.id}
                data-is-unread={!message.isRead}
                data-recipient-id={message.recipientId}
                senderId={message.senderId}
                content={message.content}
                timestamp={message.timestamp}
                isDelivered={message.isDelivered}
                isRead={message.isRead}
                isBookingRequest={message.isBookingRequest}
                contentType={message.contentType}
                attachmentUrl={message.attachmentUrl}
                attachmentType={message.attachmentType}
                senderName={message.senderName}
                senderAvatar={message.senderId === chatPartner.id ? chatPartner.avatar : null}
                onDelete={deleteMessage && message.senderId === userId ? () => deleteMessage(message.id) : undefined}
                messageId={message.id}
              />
            ))}
            {/* Élément vide à la fin pour le défilement automatique */}
            <div ref={messagesEndRef} style={{ height: '1px', clear: 'both' }}></div>
          </div>
        )}
      </div>
      
      <MessageInput
        onSendMessage={onSendMessage}
        partnerId={chatPartner?.id || ''}
        placeholder={`${t('Message')} ${chatPartner.name}...`}
      />

      <BookingDialog 
        isOpen={isBookingDialogOpen}
        onOpenChange={setIsBookingDialogOpen}
        onBookAppointment={handleBookAppointment}
        escortId={chatPartner.id}
      />
      <UserProfileModal 
        isOpen={isProfileModalOpen}
        onOpenChange={setIsProfileModalOpen}
        userProfile={clientProfile}
      />
    </>
  );
};

export default MessageDetail;
